import { getCurrentInstance, nextTick, onBeforeUnmount, ref, unref } from 'vue';
import { driver } from 'driver.js';

const DEFAULT_DELAY_MS = 180;
const DEFAULT_RETRY_DELAY_MS = 120;
const DEFAULT_MAX_ATTEMPTS = 6;

const resolveValue = (value) => (typeof value === 'function' ? value() : unref(value));

const getTimerHost = () => {
    if (typeof window !== 'undefined') {
        return window;
    }

    return globalThis;
};

export function useCoachMark({
    id,
    enabled = true,
    target,
    steps,
    canShow = true,
    storageKey = id ? `coachMark.${id}.dismissed` : null,
    storage = 'session',
    delayMs = DEFAULT_DELAY_MS,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    driverOptions = {},
} = {}, dependencies = {}) {
    const activeDriver = ref(null);
    const pendingTimeouts = new Set();
    let shouldPersistDismissal = false;

    const driverFactory = dependencies.driverFactory ?? driver;
    const nextTickFn = dependencies.nextTick ?? nextTick;
    const setTimeoutFn = dependencies.setTimeout ?? ((callback, delay) => getTimerHost().setTimeout(callback, delay));
    const clearTimeoutFn = dependencies.clearTimeout ?? ((timeoutId) => getTimerHost().clearTimeout(timeoutId));
    const getDocument = () => dependencies.document ?? (typeof document !== 'undefined' ? document : null);

    const getStorage = () => {
        if (dependencies.storage !== undefined) {
            return dependencies.storage;
        }

        if (!storage || storage === 'none') {
            return null;
        }

        if (typeof window === 'undefined') {
            return null;
        }

        try {
            return storage === 'local' ? window.localStorage : window.sessionStorage;
        } catch {
            return null;
        }
    };

    const isEnabled = () => Boolean(resolveValue(enabled));
    const canShowNow = () => Boolean(resolveValue(canShow));

    const wasDismissed = () => {
        if (!storageKey) {
            return false;
        }

        try {
            return getStorage()?.getItem?.(storageKey) === 'true';
        } catch {
            return false;
        }
    };

    const persistDismissal = () => {
        if (!storageKey) {
            return;
        }

        try {
            getStorage()?.setItem?.(storageKey, 'true');
        } catch {
            // Storage can be unavailable in private browsing, SSR, or locked contexts.
        }
    };

    const clear = () => {
        pendingTimeouts.forEach(timeoutId => {
            clearTimeoutFn(timeoutId);
        });
        pendingTimeouts.clear();
    };

    const resolveTarget = () => {
        const resolvedTarget = resolveValue(target);

        if (typeof resolvedTarget === 'string') {
            return getDocument()?.querySelector?.(resolvedTarget) ?? null;
        }

        return resolvedTarget ?? null;
    };

    const resolveSteps = (targetElement) => {
        const resolvedSteps = typeof steps === 'function' ? steps(targetElement) : steps;

        if (Array.isArray(resolvedSteps) && resolvedSteps.length > 0) {
            return resolvedSteps;
        }

        return [{ element: targetElement }];
    };

    const acknowledge = (driverInstance = activeDriver.value) => {
        shouldPersistDismissal = true;

        if (driverInstance?.destroy) {
            driverInstance.destroy();
            return;
        }

        persistDismissal();
        shouldPersistDismissal = false;
    };

    const show = (attempt = 0) => {
        if (!isEnabled() || wasDismissed() || !canShowNow()) {
            return false;
        }

        if (activeDriver.value?.isActive?.()) {
            return true;
        }

        const targetElement = resolveTarget();
        if (!targetElement) {
            if (attempt < maxAttempts) {
                queue(attempt + 1);
            }

            return false;
        }

        const resolvedSteps = resolveSteps(targetElement);
        const userOnNextClick = driverOptions.onNextClick;
        const userOnCloseClick = driverOptions.onCloseClick;
        const userOnDestroyed = driverOptions.onDestroyed;

        shouldPersistDismissal = false;
        activeDriver.value = driverFactory({
            animate: true,
            overlayOpacity: 0.24,
            stagePadding: 6,
            popoverOffset: 14,
            showButtons: ['next', 'close'],
            doneBtnText: 'Ho capito',
            ...driverOptions,
            steps: resolvedSteps,
            onNextClick: (element, step, options) => {
                if (typeof userOnNextClick === 'function') {
                    userOnNextClick(element, step, options);
                    return;
                }

                if (options.driver.isLastStep?.() === false) {
                    options.driver.moveNext?.();
                    return;
                }

                acknowledge(options.driver);
            },
            onCloseClick: (element, step, options) => {
                if (typeof userOnCloseClick === 'function') {
                    userOnCloseClick(element, step, options);
                    return;
                }

                acknowledge(options.driver);
            },
            onDestroyed: (element, step, options) => {
                if (shouldPersistDismissal) {
                    persistDismissal();
                }

                shouldPersistDismissal = false;
                activeDriver.value = null;
                userOnDestroyed?.(element, step, options);
            },
        });

        activeDriver.value.drive();
        return true;
    };

    const queue = (attempt = 0) => {
        if (!isEnabled() || wasDismissed()) {
            return null;
        }

        if (attempt === 0) {
            clear();
        }

        const timeoutId = setTimeoutFn(async () => {
            pendingTimeouts.delete(timeoutId);
            await nextTickFn();
            show(attempt);
        }, attempt === 0 ? delayMs : retryDelayMs);

        pendingTimeouts.add(timeoutId);
        return timeoutId;
    };

    const destroy = ({ persist = false } = {}) => {
        clear();
        shouldPersistDismissal = persist;

        if (activeDriver.value?.destroy) {
            activeDriver.value.destroy();
            return;
        }

        if (persist) {
            persistDismissal();
        }

        shouldPersistDismissal = false;
        activeDriver.value = null;
    };

    if (getCurrentInstance()) {
        onBeforeUnmount(() => destroy());
    }

    return {
        acknowledge,
        activeDriver,
        clear,
        destroy,
        persistDismissal,
        queue,
        show,
        wasDismissed,
    };
}

export default useCoachMark;
